import {useMemo} from 'react';

import {DocumentInputField, DocumentValue, Option, ValueResolver} from '@/engine/types';
import {useDocumentContext} from '../context/use-document-context';
import {useDocumentLocale} from './use-document-locale';

export type FieldOption = {
  id: string;
  label: string;
  value: string;
  raw: Option['value'];
};

const isOptionObject = (option: Option | Option['value']): option is Option => {
  return typeof option === 'object' && option !== null && 'value' in option;
};

const optionsToFieldOptions = (
  options: (Option | Option['value'])[] = [],
  valueResolver: ValueResolver,
  serialize: (value: DocumentValue | DocumentValue[]) => string,
): FieldOption[] => {
  return options.map(option => {
    if (isOptionObject(option)) {
      const optionValue = serialize(option.value);

      return {
        id: optionValue,
        label: valueResolver(option.label),
        value: optionValue,
        raw: option.value,
      };
    } else {
      const optionValue = serialize(option);

      return {
        id: optionValue,
        label: valueResolver(option),
        value: optionValue,
        raw: option,
      };
    }
  });
};

export const useFieldOptions = (field: DocumentInputField): FieldOption[] => {
  const {locale} = useDocumentLocale();
  const {valueResolver, serialize} = useDocumentContext();

  return useMemo(
    () => optionsToFieldOptions(field.options, valueResolver, serialize),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- should recompute on locale change
    [field.options, valueResolver, serialize, locale],
  );
};
